<template>
    <v-container v-if="guest">
        <v-label>{{$t('guest.edit')}}</v-label>
        <v-form ref="form"
                v-model="isFormValid">
            <v-container class="px-0">
                <v-row v-show="false">
                        <v-text-field outlined
                                  :label="$t('guest.id')"
                                  :placeholder="$t('guest.id')"
                                  readonly
                                  v-model="guest.id"

                                  :rules="guestIdRules"></v-text-field>
                </v-row>
                <v-row v-show="false">
                    <v-select outlined
                              readonly
                              :label="$t('guest.company')"
                              :placeholder="$t('guest.company')"
                              :items="companyOptions"
                              v-model="guest.companyId"
                              :rules="companyRules"></v-select>
                </v-row>
                <v-row>
                    <v-col class="py-0">
                        <v-text-field outlined
                                  label="Username*"
                                  placeholder=" "
                                  v-model="guest.username"
                                  :rules="usernameRules"></v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col class="py-0">
                        <v-checkbox class="my-0"
                            v-model="showPassword"
                            :label="$t('user.change_password')" >
                        </v-checkbox>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col class="py-0">
                        <v-text-field outlined
                            :label="$t('user.password')"
                            :placeholder="$t('user.password')"
                            v-model="password"
                            :rules="passwordRules"
                            :append-icon="revealPasswordText ? 'mdi-eye' : 'mdi-eye-off'"
                            :type="revealPasswordText ? 'text' : 'password'"
                            @click:append="revealPasswordText = !revealPasswordText"
                            v-on:input="changePassword"
                            v-if="showPassword"></v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col class="py-0">
                        <v-text-field
                            outlined
                            ref="confirmPasswordEle"
                            :label="$t('user.confirm_password')"
                            :placeholder="$t('user.confirm_password')"
                            v-model="confirmPassword"
                            :rules="confirmPasswordRules"
                            :append-icon="revealConfirmPasswordText ? 'mdi-eye' : 'mdi-eye-off'"
                            :type="revealConfirmPasswordText ? 'text' : 'password'"
                            @click:append="revealConfirmPasswordText = !revealConfirmPasswordText"
                            v-if="showPassword"></v-text-field>
                    </v-col>
                </v-row>
            </v-container>
        </v-form>
        
        <v-card elevation="0">
            <v-card-title class="px-0">
                {{$t('Events Attending')}} ({{selectedEvents.length}})
            </v-card-title>
            <v-card-text class="px-0">
                <v-row v-show="false">
                    <v-col>
                        <v-text-field v-model="search"
                                    append-icon="mdi-magnify"
                                    :label="$t('search')"
                                    single-line
                                    hide-details>
                        </v-text-field>
                    </v-col>
                </v-row>
                <v-data-table :headers="headers"
                            :items="originalSelectedEvents"
                            :loading="!selectedEvents_isReady"
                            :search="search"
                            show-select
                            v-model="selectedEvents"
                            disable-sort>
                    <template v-slot:item.start_date="{value}">
                        {{DateTime.fromISO(value).toFormat('yyyy-MM-dd')}}
                    </template>
                    <template v-slot:item.end_date="{value}">
                        {{DateTime.fromISO(value).toFormat('yyyy-MM-dd')}}
                    </template>
                </v-data-table>
                
                <v-row>
                    <v-col class="py-0">
                        <v-btn color="primary" @click="saveGuest" :disabled="!selectedEvents_isReady">
                            {{$t('save')}}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>
    </v-container>
</template>

<script>
    import {computed, onMounted, ref} from "@vue/composition-api";
    import {useFind, useGet} from "feathers-vuex";
    import {DateTime} from "luxon";
    import _ from 'lodash';

    export default {
        name: 'GuestEdit',
        setup(props, {root}) {
            const {Event, EventGuest, Guest, Company} = root.$FeathersVuex.api;

            const guestId = computed(() => root.$route.params.guestId);

            const {item: guest} = useGet({model: Guest, id: guestId.value});
            
            const selectedEvents_isReady = ref(false);

            // Invited Guests
            const originalSelectedEvents = ref([]);
            const selectedEvents = ref([]);
            const eventGuestParams = computed(() => {
                return {
                    query: {
                        guestId: guestId.value,
                    }
                }
            });
            const getInvitedEvents = async () => {
                selectedEvents_isReady.value = false;
                const {data: eventGuests} = await EventGuest.find(eventGuestParams.value);
                const eventIds = eventGuests.map((eg) => eg.eventId);
                
                const {data: events} = await Event.find({
                    query: {
                        id: { $in: eventIds, },
                    }
                });

                originalSelectedEvents.value = Array.from(events);
                selectedEvents.value = Array.from(events).filter(item => (item.companyId == guest.value.companyId));
                selectedEvents_isReady.value = true;
            };

            // Data Table
            const headers = ref([
                {text: root.$t('event.name'), value: 'name'},
                {text: root.$t('event.start_date'), value: 'start_date'},
                {text: root.$t('event.end_date'), value: 'end_date'},
            ]);

            onMounted(() => {
                getInvitedEvents();
            });

            const saveEventGuests = async () => {
                let removedEvents = _.differenceWith(originalSelectedEvents.value, selectedEvents.value);
                try {
                    if (removedEvents.length !== 0) {
                        const {data: removedEventGuests} = await EventGuest.find({
                            query: {
                                guestId: guestId.value,
                                eventId: {
                                    $in: removedEvents.map((e) => e.id),
                                }
                            }
                        });

                        await Promise.all([...removedEventGuests.map((eg) => eg.remove())]);
                    }
                    root.$store.dispatch('showSnackBar', root.$t('Guest account successfully saved.'));

                    getInvitedEvents();
                } catch (e) {
                    root.$handleFeatherError(e);
                    
                    getInvitedEvents();
                }
            };
            
            const form = ref(null);

            const usernameRules = ref([
                value => !!value || 'Required',
            ]);

            const nameRules = ref([
                value => !!value || 'Required',
            ]);

            const emailRules = ref([
                value => /^[^@]+@[^@]+\.[^@]+$/.test(value) || 'Invalid Email formant ',
            ]);

            const tokenRules = ref([
                value => !!value || 'Required',
            ]);

            const guestIdRules = ref([
                value => !!value || 'Required',
            ]);

            const passwordRules = ref([
                value => ((value || '').length == 0 || (value || '').length >= 4) || 'Min 4 characters',
            ]);
            const confirmPasswordRules = ref([
                // value => !!value || 'Required',
                value => (value == password.value) || 'Password Not Match',
            ]);
            const changePassword = ()=>{
                confirmPasswordEle.value.validate();
            };

            const companyParams = computed(() => {
                return {
                    query: {
                        $sort: {
                            name: 1,
                        }
                    }
                }
            });

            const updateMetaData = function(){
                guest.value.metadata = JSON.stringify({
                    "Last Name":guest.value.ev_lastname,
                    "Employee ID":guest.value.ev_employeeid,
                    "Agency ID":guest.value.ev_agencyid,
                    "Salutation":guest.value.ev_title,
                    "Email":guest.value.ev_email,
                    "Company":guest.value.ev_company,
                    "Department":guest.value.ev_department,
                    "Position":guest.value.ev_job_title,
                    "Office #":guest.value.ev_office_phone,
                    "Country Code":guest.value.ev_country_code,
                    "Mobile #":guest.value.ev_mobile,
                    "Qrcode":guest.value.ev_token,
                    "Remarks":guest.value.ev_remarks,
                    "Sms":guest.value.ev_sms,
                    "vip(1/0)":guest.value.ev_vip_flag,
                    "No reminder(1/0)":guest.value.ev_exclude_email_reminder
                })
            }
            const companyData = useFind({model: Company, params: companyParams});
            const companyOptions = computed(() => {
                return companyData.items.value.map(c => {
                    return {text: c.name, value: c.id}
                });
            });
            const companyRules = ref([
                value => !!value || 'Required',
            ]);

            const isFormValid = ref(false);
            const password = ref("");
            const confirmPassword = ref("");
            const confirmPasswordEle = ref(null);
            const revealPasswordText = ref(false);
            const revealConfirmPasswordText = ref(false);
            const showPassword = ref(false);

            const saveGuest = () => {
                selectedEvents_isReady.value = false;
                updateMetaData();
                if (isFormValid.value) {
                    if (!showPassword.value) {
                        delete guest.value.password;
                    } else {
                        guest.value.password = password.value;
                    }
                    
                    guest.value.save().then((guest) => {
                        guest.password = "";
                        // root.$store.dispatch('showSnackBar', 'Saved.');
                        saveEventGuests();
                    }, root.$handleFeatherError.bind(root));
                } else {
                    form.value.validate();
                    selectedEvents_isReady.value = true;
                }
            };

            return {
                selectedEvents,

                headers,
                search: ref(''),

                DateTime,
                getFileUrl: root.$getFileURL,

                // saveEventGuests,
                selectedEvents_isReady,
                originalSelectedEvents,
                
                guest,
                usernameRules,
                nameRules,
                emailRules,
                tokenRules,
                passwordRules,
                confirmPasswordRules,
                revealPasswordText,
                revealConfirmPasswordText,
                guestIdRules,
                companyOptions,
                companyRules,
                isFormValid,
                saveGuest,
                showPassword,
                changePassword,
                password,
                confirmPassword,
                confirmPasswordEle,
                form,
                updateMetaData,

            }
        },
    }
</script>